/**
 * Jobs Keyboard Shortcuts Hook
 *
 * Provides keyboard shortcut handlers for bulk actions on jobs.
 * Following the Asset table pattern from useAssetActions.ts
 *
 * Keyboard shortcuts:
 * - R: Rerun selected jobs
 * - C: Cancel selected jobs
 *
 * Risk Level: Low
 * - Simple keyboard mapping logic
 * - Delegates actual operations to action handlers
 */

import { useCallback } from 'react';

import type { Job } from '@/types';

/**
 * Job action handlers interface
 */
interface JobsActionHandlers {
  handleRerun: (jobs: Job[]) => Promise<void>;
  handleCancel: (jobs: Job[]) => Promise<void>;
}

/**
 * Parameters for useJobsKeyboard hook
 */
interface UseJobsKeyboardParams {
  /** Currently selected jobs */
  selectedJobs: Job[];
  /** Action handlers for job operations */
  handlers: JobsActionHandlers;
}

/**
 * Return type for useJobsKeyboard hook
 */
interface JobsKeyboardReturn {
  /** Handler for keyboard actions */
  handleKeyboardAction: (code: string, jobs: Job[]) => void;
}

/**
 * Hook for Jobs table keyboard shortcuts
 *
 * Provides keyboard shortcut handlers for bulk actions on jobs
 * following the Asset table pattern from useAssetActions.ts
 *
 * Supported shortcuts:
 * - KeyR: Rerun selected jobs
 * - KeyC: Cancel selected jobs
 *
 * @param params - Configuration object with dependencies
 * @returns Object containing keyboard action handler
 *
 * @example
 * ```tsx
 * const handlers = useJobsActionHandlers({ selectedJobs, refetch });
 * const { handleKeyboardAction } = useJobsKeyboard({
 *   selectedJobs,
 *   handlers,
 * });
 *
 * // Use in Table keyboardActions prop
 * <Table
 *   keyboardActions={{
 *     codes: ['KeyR', 'KeyC'],
 *     onKeyboardAction: handleKeyboardAction,
 *   }}
 * />
 * ```
 */
export const useJobsKeyboard = ({
  selectedJobs: _selectedJobs,
  handlers,
}: UseJobsKeyboardParams): JobsKeyboardReturn => {
  /**
   * Handles keyboard shortcuts for bulk job actions
   *
   * Maps keyboard codes to job operations:
   * - KeyR: Rerun jobs
   * - KeyC: Cancel jobs
   *
   * @param code - Keyboard code (e.g., 'KeyR', 'KeyC')
   * @param jobs - Array of jobs to perform action on
   */
  const handleKeyboardAction = useCallback(
    (code: string, jobs: Job[]) => {
      switch (code) {
        case 'KeyR':
          // R = Rerun selected jobs
          handlers.handleRerun(jobs);
          break;
        case 'KeyC':
          // C = Cancel selected jobs
          handlers.handleCancel(jobs);
          break;
        default:
          // Unknown keyboard code - do nothing
          break;
      }
    },
    [handlers]
  );

  return {
    handleKeyboardAction,
  };
};
